import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { debounce, xor } from 'lodash';
import { getDrivers } from '../../redux/actions/drivers';

import {
  Form,
  Notification,
  DriverCreateForm,
  DriverList,
  RouteWrapper,
  Pagination,
  Section,
  HeadingSection,
  Content,
} from '../../components';

import PAGINATE from '../../data/paginate';

interface Props {
  drivers: any;
  isFetching: boolean;
  dispatch: Function;
}

interface State {
  page: number;
  search: string;
  isMergingDrivers: boolean;
  mergeDriversList: number[];
  isFetching: boolean;
  isAddingDriver: boolean;
  error: string | boolean;
}

class Route extends React.Component<Props, State> {
  state = {
    page: 0,
    search: '',
    isMergingDrivers: false,
    mergeDriversList: [] as number[],
    isFetching: false,
    isAddingDriver: false,
    error: false,
  };

  static MAX_DRIVERS = 2;

  componentDidMount() {
    this.props.dispatch(getDrivers(undefined, this.state.page));
  }

  showDriversMerge = () => {
    this.setState({ isMergingDrivers: true });
  };

  hideDriversMerge = () => {
    this.setState({ isMergingDrivers: false, mergeDriversList: [] });
  };

  toggleDriver = (driver) => {
    if (
      this.state.mergeDriversList.length >= Route.MAX_DRIVERS &&
      this.state.mergeDriversList.indexOf(driver.id) === -1
    ) {
      return;
    }
    this.setState({
      mergeDriversList: xor(this.state.mergeDriversList, [driver.id]),
    });
  };

  mergeDrivers = async () => {
    if (this.state.mergeDriversList.length < 2) {
      return;
    }
    this.props.dispatch(
      push(
        `/drivers/${this.state.mergeDriversList[0]}/merge/${this.state.mergeDriversList[1]}`,
      ),
    );
  };

  private get canMerge() {
    return this.state.mergeDriversList.length > 1;
  }

  render() {
    return (
      <RouteWrapper
        requireAuth
        requirePermission="MANAGE_DRIVERS"
        title="Drivers"
        loading={this.props.isFetching}
      >
        <HeadingSection
          title="Drivers"
          controls={[
            <button
              key="add"
              className={`is-${
                this.state.isAddingDriver ? 'primary' : 'default'
              }`}
              onClick={() =>
                this.setState({ isAddingDriver: !this.state.isAddingDriver })
              }
            >
              Add Driver
            </button>,
            <button
              key="merge"
              className={`is-${
                this.state.isMergingDrivers ? 'primary' : 'default'
              }`}
              onClick={() => {
                this.setState({
                  isMergingDrivers: !this.state.isMergingDrivers,
                  mergeDriversList: [],
                });
              }}
            >
              Merge Drivers
            </button>,
          ]}
        />
        <Section>
          <Content>
            {this.state.isAddingDriver ? (
              <DriverCreateForm
                isTypeForm={false}
                onUpdate={async (response) => {
                  await this.props.dispatch(getDrivers());
                  this.setState({ isAddingDriver: false });
                }}
              />
            ) : (
              <>
                {this.state.isMergingDrivers && (
                  <Notification type="default">
                    <p>Select the drivers you want to merge</p>
                    {this.state.mergeDriversList.length > 1 && (
                      <button
                        onClick={this.mergeDrivers}
                        className="is-primary"
                      >
                        Merge
                      </button>
                    )}
                  </Notification>
                )}
                <div className="field">
                  <Form
                    type="search"
                    fields={[{ name: 'search', type: 'text' }]}
                    hasButton={false}
                    handleChange={debounce(({ search }) => {
                      this.setState({ search, page: 0 }, () =>
                        this.props.dispatch(getDrivers(undefined, 0, search)),
                      );
                    }, 200)}
                  />
                </div>
                {(!this.props.isFetching || this.props.drivers.length > 0) && (
                  <>
                    <div className="field">
                      <DriverList
                        hideSearch
                        handleClick={(driver) => {
                          if (this.state.isMergingDrivers) {
                            this.toggleDriver(driver);
                            return;
                          }
                          this.props.dispatch(push(`/drivers/${driver.id}`));
                        }}
                        isSelected={(id) =>
                          this.state.mergeDriversList.indexOf(id) !== -1
                        }
                        items={this.props.drivers.slice(
                          0,
                          PAGINATE.drivers.MAX,
                        )}
                      />
                    </div>
                    <div className="field">
                      <Pagination
                        page={this.state.page}
                        lastPage={
                          this.props.drivers.length <= PAGINATE.drivers.MAX
                        }
                        handleUpdate={async (page) => {
                          this.setState({ page });
                          await this.props.dispatch(
                            getDrivers(undefined, page, this.state.search),
                          );
                        }}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </Content>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ drivers }) => ({
  isFetching: drivers.isFetching,
  drivers: drivers.drivers,
});

export default connect(mapStateToProps)(Route);
